/**
 * FFmpegMemoryGuard — predict peak memory for an FFmpeg WASM operation and refuse
 * early with a user-readable error instead of letting the process explode with
 * "RuntimeError: memory access out of bounds".
 *
 * Prod telemetry showed ~150 OOM crashes / week across enhance-video, join,
 * video-crop, audio-compressor, etc. Big files (>200 MB) were the consistent
 * culprit — FFmpeg's virtual FS holds the input, frame buffers during decode,
 * filter-graph temporaries, and output muxer state all at once. On mid-range
 * hardware (8 GB RAM device, browser capped at ~2 GB per tab) that stacks up
 * fast for transcode / upscale operations.
 *
 * Usage:
 *   const guard = FFmpegMemoryGuard.check({
 *       file: videoFile,                 // File or { size, type, duration? }
 *       operation: 'upscale-2x',         // see OPERATION_MULTIPLIERS
 *       expectedFormat: 'mp4'            // optional — used when encoder cost differs
 *   });
 *   if (!guard.safe) {
 *       __toast(guard.userMessage, 'error');
 *       __aeBlocked({ error_type: 'memory_guard', reason: guard.reason, predictedMB: guard.predictedMB });
 *       return;
 *   }
 *
 * Operation multipliers are empirically tuned against the "RuntimeError: memory
 * access out of bounds" telemetry cluster — they err toward refusing borderline
 * cases because a false negative here means a cryptic crash that loses the user's
 * work, while a false positive means the user sees a helpful message and picks a
 * smaller file.
 */
(function(){
'use strict';

// Multiplier over the input file size — how many "file sizes" of RAM the operation
// needs at peak. Derived from observed crash patterns + FFmpeg memory-behavior docs.
var OPERATION_MULTIPLIERS = {
    // Light-touch — mostly remux, short filter chain
    'trim':               1.8,
    'copy':               2.5,      // Calibrated 2026-04-24 from perf{f:ffmpeg_mem,op:'copy'} — real avg 3.5, raising from 1.3 to 2.5 gives safety margin without blocking typical small copy flows. Still below the streaming path's 0.4 avg, which is the recommended route for large copy files anyway.
    'remux':              1.3,
    'join':               2.5,      // concat demuxer holds segments in memory
    'volume':             2.0,
    'channel-map':        2.0,
    'metadata':           1.3,

    // Audio re-encode
    'audio-transcode':    3.0,
    'audio-filter':       3.5,       // eq, compressor, normalize
    // Re-tuned 5.0 → 9.0 on 2026-05-16. feedback #420 (Наталья): a 24 MB
    // WAV on a 4 GB device crashed the tab at hum/max strength — 24 × 5.0
    // = 120 MB predicted, under the 256 MB low-RAM cap, so the guard said
    // "safe" and the monolithic FFmpeg path then OOM-killed the tab. The
    // max-strength classical chain stacks adeclick (whole-timeline window
    // buffer) + afftdn (FFT overlap buffers) + up to 6×bandreject, so real
    // peak is ~10-15× input, not 5×. 9.0 is the honest mid-point across
    // light→max. NOTE: the primary OOM defence is now chunked routing in
    // the tool blade (denoise runWasm → runWasmChunked on low-RAM); this
    // multiplier is the secondary guard + keeps predictedMB telemetry honest.
    'denoise':            9.0,       // adeclick + afftdn + bandreject stack — whole-timeline + FFT overlap buffers
    'pitch-shift':        4.0,       // asetrate + atempo chain
    'time-stretch':       4.0,
    'silence-remove':     3.5,

    // ONNX/WASM audio-to-notes pipeline. The multiplier-by-file-size
    // heuristic is INHERENTLY noisy for compressed audio because the
    // actual memory need scales with decoded *duration*, not file
    // size: a 6 MB 320 kbps MP3 is ~150 s but a 6 MB 128 kbps MP3 is
    // ~390 s. So this multiplier is intentionally lenient — only
    // catches obviously huge uploads (50+ MB) before decode. The real
    // OOM defence happens AFTER decode via the duration-cap slicing
    // in the tool blade itself (resample → if duration > 90 s on
    // deviceMemory ≤ 4, slice and show a partial-transcription
    // info toast). See feedback #407 (Юрий, 2026-05-15).
    'audio-to-notes':     5.0,

    // Video re-encode
    'video-transcode':    6.0,       // h264 → h264 re-encode
    'video-filter':       7.0,       // colour / crop / overlay
    'video-enhance':      10.0,      // sharpen + denoise + tune
    'upscale-2x':         14.0,      // lanczos / nnedi / sinc at 2×
    'upscale-4x':         26.0,      // 4× is brutal on memory
    'upscale-8x':         50.0,

    // Generic / unknown
    'default':            5.0
};

function lookupMultiplier(operation){
    if (!operation) return OPERATION_MULTIPLIERS.default;
    return OPERATION_MULTIPLIERS[operation] != null
        ? OPERATION_MULTIPLIERS[operation]
        : OPERATION_MULTIPLIERS.default;
}

// Browser's usable memory per tab, in bytes. Conservative: even on 8 GB devices,
// Chrome typically caps each tab's JS heap around 2 GB, and we need room for the
// FFmpeg WASM core itself (~40 MB) + DOM + other tool state. 25% of reported
// navigator.deviceMemory keeps us under that ceiling on 8 GB devices and doesn't
// punish 16 GB+ desktops too hard.
//
// Low-end Android (deviceMemory ≤ 4 — typical for 4 GB phones on Android 8-9
// with Yandex Browser / older Chrome) hits a much harder ceiling: 32-bit
// Chromium processes cap tab JS heap around 512 MB total, and after the page
// chrome / DOM / fonts / running ad scripts there's usually ~200-300 MB
// free for our pipeline. The 25% rule would tell us "1 GB OK" on 4 GB,
// which is way too optimistic — feedback #407 (Юрий, 2026-05-15) hit
// "memory access out of bounds" on a 6.5 MB MP3 that the old guard would
// have happily admitted. Cap low-end devices at 256 MB instead.
// Hard ceiling, independent of device RAM — MEASURED, not derived
// (2026-08-06, `-c copy` remux of a 2.2 GB file through the real
// processStreaming lane on a 32 GB machine):
//
//   - emscripten MEMFS keeps file CONTENT in the worker's JS heap, NOT in
//     the wasm32 linear memory (a probe wrote 2560 MB of files into the FS
//     of a core whose wasm maximum is 2048 MB — the old "wasm heap" theory
//     that first justified this constant was WRONG about the mechanism);
//   - the real bound is the worker's ArrayBuffer budget: the remux died at
//     **1897 MB of output written** with `RangeError: Array buffer
//     allocation failed` on the MEMFS growth realloc;
//   - `-movflags +faststart` (auto-added for mp4-family) rewrites the
//     finished output through a temp copy, so the END of a run needs
//     ~2× output — which is why a ~1.5 GB-output encode grinds for 90
//     minutes and dies AT the finish line (user 4126, 778 MB / 2 h 03 m,
//     «После 100% трансформации сервис не даёт возможности скачать»).
//
// 1792 MB admit keeps the classic in-memory path safely under the measured
// 1.9 GB death line; checkVideoEncode() below charges output ×2 against it,
// which lands the practical output bound near ~770 MB — comfortably below
// the ~950 MB where output+faststart-temp crosses the measured ceiling.
// The old 25%-of-RAM rule alone told a 32 GB desktop "8 GB is fine".
var WASM_HEAP_ADMIT_BYTES = 1792 * 1024 * 1024;

/* ⚠️ The RAM figure comes from window.__memBudget, never from
   navigator.deviceMemory directly.
   ────────────────────────────────────────────────────────────────────────
   That API is ABSENT in all of WebKit and in Firefox, so `deviceMemory || 2`
   put desktop Firefox and desktop Safari on the 2 GB assumption — whose branch
   below is the 256 MB cap. A 32 GB desktop running Firefox was therefore told
   it had 256 MB, seven times under the 1792 MB the same machine gets in Chrome,
   and every file over ~43 MB was declared too large for a `video-transcode`.
   That is bug #1526's second half: Артур (Firefox/Windows) was told his device
   could not do the job, was offered the paid server lane on that premise, and
   then the job finished in 85 minutes.

   The irony is that this file already KNEW: the note on encodeStateBytes()
   below spells the trap out, and checkVideoEncode() was written around it —
   while deviceCapBytes(), which every other entry point goes through, kept the
   original expression. A fact recorded next to one caller is not a fix.

   __memBudget (layouts/app.blade.php) is the owner of "how much memory may I
   assume": 2 GB for iOS-WebKit (a per-tab WKWebView ceiling, not a RAM
   estimate), 8 GB for desktop Firefox/Safari, and whatever the browser reports
   otherwise. Measured effect here: iOS and low-RAM Android keep the 256 MB cap
   they should have (≈560 of the blocks in 45 d), desktop Firefox/Safari stop
   being refused work they can plainly do. */
function deviceMemoryGB(){
    var B = window.__memBudget;
    if (B && typeof B.gb === 'function') return B.gb();
    return navigator.deviceMemory || 2;   // primitive not loaded yet (tests, early calls)
}

function deviceCapBytes(){
    var deviceMemMB = deviceMemoryGB() * 1024;
    if (deviceMemMB <= 4096) {
        return 256 * 1024 * 1024;
    }
    return Math.min(deviceMemMB * 1024 * 1024 * 0.25, WASM_HEAP_ADMIT_BYTES);
}

/* A device whose whole budget is the low tier. Used as the "is this machine
   actually constrained" term instead of sniffing for a phone: measured over
   5 737 runs the two agree on all but 3 of them, and a budget is the thing that
   is true, while a form factor is a proxy for it. */
function isConstrainedDevice(){
    return deviceCapBytes() <= 256 * 1024 * 1024;
}

function localizedMessage(loc, predictedMB, capMB){
    var L = (loc || (window.__LOCALE) || 'en').substring(0, 2);
    var msgs = {
        en: 'This file is too large to process in the browser on this device (' + predictedMB + ' MB predicted peak, device cap ' + capMB + ' MB). Try a shorter clip, lower resolution, or close other tabs.',
        ru: 'Файл слишком большой для обработки в браузере на этом устройстве (оценка ' + predictedMB + ' МБ пика, лимит устройства ' + capMB + ' МБ). Попробуйте более короткий ролик, меньшее разрешение или закройте другие вкладки.',
        ko: '이 기기의 브라우저에서 처리하기에는 파일이 너무 큽니다 (예상 ' + predictedMB + ' MB, 기기 한도 ' + capMB + ' MB). 더 짧거나 해상도가 낮은 클립을 사용하거나 다른 탭을 닫아주세요.',
        ar: 'هذا الملف كبير جداً للمعالجة في المتصفح على هذا الجهاز (الذروة المتوقعة ' + predictedMB + ' ميغابايت، حد الجهاز ' + capMB + ' ميغابايت). جرّب مقطعاً أقصر أو دقة أقل أو أغلق علامات تبويب أخرى.',
        id: 'File ini terlalu besar untuk diproses di browser pada perangkat ini (puncak prediksi ' + predictedMB + ' MB, batas perangkat ' + capMB + ' MB). Coba klip yang lebih pendek, resolusi lebih rendah, atau tutup tab lain.',
        pt: 'Este arquivo é muito grande para processar no navegador neste dispositivo (pico previsto ' + predictedMB + ' MB, limite ' + capMB + ' MB). Tente um clipe mais curto, resolução menor ou feche outras abas.',
        de: 'Diese Datei ist zu groß, um auf diesem Gerät im Browser verarbeitet zu werden (Spitzenwert geschätzt ' + predictedMB + ' MB, Gerätelimit ' + capMB + ' MB). Verwenden Sie einen kürzeren Clip, eine niedrigere Auflösung oder schließen Sie andere Tabs.',
        es: 'Este archivo es demasiado grande para procesar en el navegador en este dispositivo (pico estimado ' + predictedMB + ' MB, límite del dispositivo ' + capMB + ' MB). Prueba un clip más corto, resolución menor o cierra otras pestañas.',
        fr: 'Ce fichier est trop volumineux pour être traité dans le navigateur sur cet appareil (pic estimé à ' + predictedMB + ' Mo, limite de l\'appareil ' + capMB + ' Mo). Essayez un extrait plus court, une résolution inférieure ou fermez d\'autres onglets.',
        uk: 'Цей файл завеликий для обробки у браузері на цьому пристрої (очікуваний пік ' + predictedMB + ' МБ, ліміт пристрою ' + capMB + ' МБ). Спробуйте коротший ролик, меншу роздільну здатність або закрийте інші вкладки.',
        tr: 'Bu dosya bu cihazda tarayıcıda işlenemeyecek kadar büyük (tahmini pik ' + predictedMB + ' MB, cihaz sınırı ' + capMB + ' MB). Daha kısa bir klip, daha düşük çözünürlük deneyin veya diğer sekmeleri kapatın.'
    };
    return msgs[L] || msgs.en;
}

function check(opts){
    opts = opts || {};
    var file = opts.file;
    if (!file || !file.size) {
        return { safe: true, predictedMB: 0, capMB: Math.round(deviceCapBytes() / 1048576), reason: 'no_file' };
    }
    var size = file.size;
    var multiplier = lookupMultiplier(opts.operation);
    var predictedBytes = size * multiplier;
    var capBytes = deviceCapBytes();
    var predictedMB = Math.ceil(predictedBytes / 1048576);
    var capMB = Math.ceil(capBytes / 1048576);

    if (predictedBytes <= capBytes) {
        return { safe: true, predictedMB: predictedMB, capMB: capMB, operation: opts.operation || 'default', multiplier: multiplier };
    }

    return {
        safe: false,
        reason: 'file_too_large_for_operation',
        predictedMB: predictedMB,
        capMB: capMB,
        operation: opts.operation || 'default',
        multiplier: multiplier,
        userMessage: localizedMessage(window.__LOCALE, predictedMB, capMB)
    };
}

// Convenience: caller wants to guard and fail-fast. Throws a typed Error with
// err.code = 'FFMPEG_MEMORY_GUARD' on refusal — tools can catch specifically
// and surface the user message without a generic crash.
function assertSafe(opts){
    var r = check(opts);
    if (!r.safe) {
        var err = new Error(r.userMessage);
        err.code = 'FFMPEG_MEMORY_GUARD';
        err.predictedMB = r.predictedMB;
        err.capMB = r.capMB;
        err.operation = r.operation;
        throw err;
    }
    return r;
}

// Localized "this conversion would need too much memory" wording specific to
// dimension-driven costs (GIF encode, palettegen+paletteuse, video-frame
// pixel buffers). Distinct from localizedMessage because the actionable
// advice is different: the user can lower THIS conversion's resolution
// or duration instead of trying a smaller input file.
function localizedDimensionsMessage(loc, predictedMB, capMB){
    var L = (loc || (window.__LOCALE) || 'en').substring(0, 2);
    var msgs = {
        en: 'This conversion would need ~' + predictedMB + ' MB of memory (browser cap ~' + capMB + ' MB on this device). Try a lower resolution, fewer frames per second, or a shorter clip.',
        ru: 'Этой конверсии нужно ~' + predictedMB + ' МБ памяти (лимит браузера ~' + capMB + ' МБ на этом устройстве). Попробуйте меньшее разрешение, меньше кадров в секунду или более короткий ролик.',
        ko: '이 변환에는 ~' + predictedMB + ' MB 메모리가 필요합니다 (이 기기 브라우저 한도 ~' + capMB + ' MB). 더 낮은 해상도, 낮은 FPS 또는 더 짧은 클립을 시도하세요.',
        ar: 'يحتاج هذا التحويل إلى ~' + predictedMB + ' ميغابايت من الذاكرة (حد المتصفح ~' + capMB + ' ميغابايت على هذا الجهاز). جرّب دقة أقل أو إطارات أقل في الثانية أو مقطعاً أقصر.',
        id: 'Konversi ini membutuhkan ~' + predictedMB + ' MB memori (batas browser ~' + capMB + ' MB di perangkat ini). Coba resolusi lebih rendah, FPS lebih rendah, atau klip lebih pendek.',
        pt: 'Esta conversão precisaria de ~' + predictedMB + ' MB de memória (limite do navegador ~' + capMB + ' MB neste dispositivo). Tente resolução menor, menos quadros por segundo ou clipe mais curto.',
        de: 'Diese Konvertierung würde ~' + predictedMB + ' MB Speicher benötigen (Browser-Limit ~' + capMB + ' MB auf diesem Gerät). Verwenden Sie eine niedrigere Auflösung, weniger FPS oder einen kürzeren Clip.',
        es: 'Esta conversión necesitaría ~' + predictedMB + ' MB de memoria (límite del navegador ~' + capMB + ' MB en este dispositivo). Prueba menor resolución, menos FPS o un clip más corto.',
        fr: 'Cette conversion nécessiterait ~' + predictedMB + ' Mo de mémoire (limite du navigateur ~' + capMB + ' Mo sur cet appareil). Essayez une résolution plus basse, moins de FPS ou un extrait plus court.',
        uk: 'Цьому перетворенню потрібно ~' + predictedMB + ' МБ памʼяті (ліміт браузера ~' + capMB + ' МБ на цьому пристрої). Спробуйте меншу роздільну здатність, менше кадрів за секунду або коротший ролик.',
        tr: 'Bu dönüştürme için ~' + predictedMB + ' MB bellek gerekir (bu cihazda tarayıcı sınırı ~' + capMB + ' MB). Daha düşük çözünürlük, daha az kare/saniye veya daha kısa bir klip deneyin.'
    };
    return msgs[L] || msgs.en;
}

// Phase Y (2026-06-04) — dimension-based estimate for pixel-graph operations
// (GIF encode via palettegen+paletteuse, frame extraction, etc.). The
// file-size × multiplier model in check() doesn't fit these: a 2 MB MP4
// at 1080p produces a 5 GB RGB24 buffer if the user picks 60 s × 60 fps,
// while a 100 MB MP4 at 480p produces only a 200 MB buffer. Memory cost
// is dominated by (width × height × frames × bytesPerPixel) plus the
// filter-graph overhead (palettegen → palette buffer + paletteuse → diff
// buffer, ~2x baseline).
//
// /pino/streams 2026-06-04: 35 events / 7d on video-to-gif hit
// "Out of memory | Failed to inject frame into filter network" with
// libvpx encoder. The user wasted minutes waiting for FFmpeg WASM to
// load + run partial encode before OOM. Predict upfront and tell them
// what to change BEFORE wasting the WASM dive.
//
// opts: { width, height, frames, bytesPerPixel?=3, filterOverhead?=2.0 }
function checkDimensions(opts){
    opts = opts || {};
    var w = +opts.width || 0;
    var h = +opts.height || 0;
    var frames = +opts.frames || 0;
    var bpp = +opts.bytesPerPixel || 3;
    var overhead = +opts.filterOverhead || 2.0;
    var capBytes = deviceCapBytes();
    var capMB = Math.ceil(capBytes / 1048576);
    if (w <= 0 || h <= 0 || frames <= 0) {
        return { safe: true, predictedMB: 0, capMB: capMB, reason: 'no_dimensions' };
    }
    var predictedBytes = w * h * frames * bpp * overhead;
    var predictedMB = Math.ceil(predictedBytes / 1048576);
    if (predictedBytes <= capBytes) {
        return {
            safe: true,
            predictedMB: predictedMB,
            capMB: capMB,
            width: w, height: h, frames: frames,
            bytesPerPixel: bpp, filterOverhead: overhead
        };
    }
    return {
        safe: false,
        reason: 'dimensions_too_large_for_browser',
        predictedMB: predictedMB,
        capMB: capMB,
        width: w, height: h, frames: frames,
        bytesPerPixel: bpp, filterOverhead: overhead,
        userMessage: localizedDimensionsMessage(window.__LOCALE, predictedMB, capMB)
    };
}

// ── Duration-driven estimate for whole-file video RE-ENCODES ────────────────
// The file-size × multiplier model in check() is blind to the real killer for
// long videos: the OUTPUT. A 778 MB / 2 h 03 m 1080p movie streams its input
// via WORKERFS (so `check()` never fires), but the re-encoded output grows in
// MEMFS (worker JS heap — measured RangeError-death at 1897 MB written, see
// WASM_HEAP_ADMIT_BYTES above), and `-movflags +faststart` (auto-added for
// mp4-family) rewrites it through a temp copy at the very end, doubling the
// footprint at the moment the progress bar reads "almost done". Such a run is
// DOOMED BEFORE IT STARTS, but the user only finds out after ~90 minutes of
// grinding (user 4126, 2026-08-05: "После 100% трансформации сервис не даёт
// возможности скачать результат").
//
// Estimate output bytes from duration × (pixels × fps × bits-per-pixel for
// the CRF) + audio, then charge: output ×2 (faststart temp copy) + input
// (only when it will NOT stream, i.e. under the 50 MB streaming threshold)
// + 256 MB codec/filter state. bpp values are x264-ultrafast ballparks on
// typical content — intentionally mid-range: this guard refuses only runs
// that are hopeless by a wide margin, borderline cases still get their shot.
//
// opts: { durationS, width, height, fps?=25, crf?='23', inputBytes?=0,
//         audioKbps?=128 }
var CRF_BPP = { '17': 0.10, '18': 0.09, '20': 0.065, '23': 0.045, '26': 0.030, '28': 0.022 };
var STREAMED_INPUT_MIN = 50 * 1024 * 1024;   // mirrors ffmpeg-auto-guard video thresholds

// Codec + filter working set. ⚠️ This was a FLAT 256 MB until 2026-08-19, and a
// flat 256 MB is not a term in a budget — it IS the whole budget on any device
// that does not report navigator.deviceMemory. Safari and Firefox never report
// it, so `deviceMemory || 2` puts them on the 2 GB assumption whose cap is
// exactly 256 MB, and the check then refused EVERY re-encode: measured, a
// 640x360 clip of 10 s scored 263 MB against a 256 MB cap. Nothing noticed
// because the only caller used the verdict as a hint rather than a refusal —
// so the first caller to trust it would have broken the FFmpeg lane for every
// Safari user.
//
// The working set tracks the FRAME, not a constant: x264 holds the current
// picture plus reference and lookahead copies, and the filter graph a couple
// more. /video-to-vr telemetry shows x264 dying on `malloc of size 22731200`
// for a 7680x2160 side-by-side frame — that is one frame's planes, i.e. the
// allocation that scales. 24 frame-equivalents of yuv420p is the mid-range of
// what veryfast keeps in flight; the 64 MB floor covers muxer and I/O buffers
// that do not shrink with the picture.
function encodeStateBytes(w, h){
    return Math.max(64 * 1024 * 1024, w * h * 1.5 * 24);
}

function checkVideoEncode(opts){
    opts = opts || {};
    var dur = +opts.durationS || 0;
    var w = +opts.width || 0, h = +opts.height || 0;
    var fps = +opts.fps || 25;
    var capBytes = Math.min(deviceCapBytes(), WASM_HEAP_ADMIT_BYTES);
    var capMB = Math.ceil(capBytes / 1048576);
    if (dur <= 0 || w <= 0 || h <= 0) {
        return { safe: true, predictedMB: 0, capMB: capMB, reason: 'no_dimensions' };
    }
    var bpp = CRF_BPP[String(opts.crf)] != null ? CRF_BPP[String(opts.crf)] : CRF_BPP['23'];
    var videoBps = w * h * fps * bpp;
    var audioBps = ((+opts.audioKbps || 128) * 1000);
    var outBytes = dur * (videoBps + audioBps) / 8;
    var inputBytes = +opts.inputBytes || 0;
    var inHeapInput = (inputBytes > 0 && inputBytes < STREAMED_INPUT_MIN) ? inputBytes : 0;
    var predictedBytes = outBytes * 2 + inHeapInput + encodeStateBytes(w, h);
    var predictedMB = Math.ceil(predictedBytes / 1048576);
    if (predictedBytes <= capBytes) {
        return { safe: true, predictedMB: predictedMB, capMB: capMB, outMB: Math.ceil(outBytes / 1048576) };
    }
    return {
        safe: false,
        reason: 'video_too_long_for_wasm_encode',
        predictedMB: predictedMB,
        capMB: capMB,
        outMB: Math.ceil(outBytes / 1048576),
        userMessage: localizedDimensionsMessage(window.__LOCALE, predictedMB, capMB)
    };
}

/* ── checkStreamingLane — for a run whose INPUT never enters the heap ───────
   check() prices a run as inputSize x multiplier. That is the right model for
   the classic in-memory lane, and the wrong one for a tool that mounts its
   input through WORKERFS: the bytes stay on disk, so the input size stops being
   the thing that is allocated. /video-compressor streams everything over
   200 MB and its output is, by construction, SMALLER than its input — yet its
   pre-flight priced a 452 MB source at 452 x 6 = 2710 MB and refused (#1526).

   So this predicate was not reasoned out, it was FITTED — on 5 737 real runs
   (compress_start joined to its outcome, 45 d) with a temporal holdout: the
   first 65 % of the period to choose it, the last 35 % to score it. Ground
   truth for "died of memory" is a reported OOM-class error OR a session that
   simply stopped emitting without a pagehide — a tab killed by the OS says
   nothing, and counting only the errors we receive undercounts deaths by 149
   to 13. Candidates were a grid over (mobile threshold x desktop threshold x
   pixel escape), plus the shipped model as the baseline:

     rule                                   test precision   test recall
     shipped (inputSize x multiplier)             25.7 %        48.5 %
     input > 300 MB                               29.3 %        40.6 %
     source pixels > 8.3 MP                       43.3 %         4.4 %
     THIS: >16.6 MP, or constrained & >250 MB     77.4 %        24.2 %

   Precision is the axis that matters here and recall is not, because this
   warning PREVENTS NOTHING: the tool runs anyway, so a caught death is a death
   that was forewarned, not avoided. What the old rule bought with its extra
   recall was 442 people told their device could not do a job it then did —
   each of them shown the paid server lane on a false premise. A claim that is
   right one time in four teaches people to ignore everything the page says.

   Two terms, and both are physical:
     · > 16.6 MP of source (8K) — one frame's planes alone are ~35 MB and x264
       keeps two dozen in flight; this is where desktops die, and it is the ONLY
       size term that survived on desktop. Plain file size predicted nothing
       there: the winning desktop threshold in the grid was "never".
     · a constrained device budget AND a large input — where 65 % of the deaths
       are. On these machines the ceiling is the tab, not the file, and a large
       input is simply the thing most likely to walk into it.

   opts: { fileBytes, width, height } */
function checkStreamingLane(opts){
    opts = opts || {};
    var bytes = +opts.fileBytes || 0;
    var w = +opts.width || 0, h = +opts.height || 0;
    var capMB = Math.ceil(deviceCapBytes() / 1048576);
    var px = w * h;
    var hugeFrame = px > 16.6e6;
    var constrainedAndBig = isConstrainedDevice() && bytes > 250 * 1024 * 1024;
    if (!hugeFrame && !constrainedAndBig) {
        return { safe: true, capMB: capMB, predictedMB: 0, reason: 'streaming_lane_ok' };
    }
    return {
        safe: false,
        reason: hugeFrame ? 'source_frame_too_large' : 'device_budget_too_small_for_input',
        capMB: capMB,
        predictedMB: 0,          // this rule is a classifier, not an estimate — say so
        megapixels: Math.round(px / 1e5) / 10,
        fileMB: Math.round(bytes / 1048576),
        userMessage: localizedMessage(window.__LOCALE, Math.round(bytes / 1048576), capMB)
    };
}

// Same fail-fast helper for the dimension-based path.
function assertDimensionsSafe(opts){
    var r = checkDimensions(opts);
    if (!r.safe) {
        var err = new Error(r.userMessage);
        err.code = 'FFMPEG_MEMORY_GUARD_DIMENSIONS';
        err.predictedMB = r.predictedMB;
        err.capMB = r.capMB;
        err.width = r.width; err.height = r.height; err.frames = r.frames;
        throw err;
    }
    return r;
}

// ── Output-size check for COPY lanes (2026-09-17) ───────────────────────────
// checkVideoEncode() above prices a re-encode from duration × frame; a copy
// lane (`-c copy`, `-c:v copy -an`) has no frame to price — its output is a
// share of the INPUT, and only the tool knows the share (the trimmed piece,
// the streams kept). The wall is the same wasm32 MEMFS: measured 2026-08-24
// on the remux-copy op, 1745 MiB written PASS, 1891 MiB «RangeError: Array
// buffer allocation failed» — 1600 MiB is the number the 'videoStream' kind
// promises, kept here as the output wall with 145 MiB of margin. Needed the
// day the input kinds grew past it: a 4 GiB zone with a 40-minute `-c copy`
// piece of a 10 GB film would grind for minutes and die at the muxing step.
// The NUMBER is owned by the layout (window.__WASM_OUTPUT_MAX — the same wall
// /video-trim and /volume already price against); the constant below is only
// the fallback for a page that loaded this file without the layout.
// opts: { outBytes }   → { safe, reason, predictedMB, capMB, userMessage }
var COPY_OUTPUT_WALL_BYTES = 1600 * 1024 * 1024;
function outputWallBytes(){
    var w = (typeof window !== 'undefined') ? +window.__WASM_OUTPUT_MAX : 0;
    return w > 0 ? w : COPY_OUTPUT_WALL_BYTES;
}

function localizedOutputMessage(loc, predictedMB, capMB){
    var L = (loc || (window.__LOCALE) || 'en').substring(0, 2);
    var msgs = {
        en: 'The result would be ~' + predictedMB + ' MB, and the largest file this browser can assemble is ~' + capMB + ' MB. Choose a shorter piece.',
        ru: 'Результат получится ~' + predictedMB + ' МБ, а собрать в браузере можно файл до ~' + capMB + ' МБ. Выберите более короткий фрагмент.',
        ko: '결과 파일이 ~' + predictedMB + ' MB가 되지만, 이 브라우저에서 만들 수 있는 최대 크기는 ~' + capMB + ' MB입니다. 더 짧은 구간을 선택하세요.',
        ar: 'سيكون حجم النتيجة ~' + predictedMB + ' ميغابايت، بينما أكبر ملف يمكن لهذا المتصفح تجميعه هو ~' + capMB + ' ميغابايت. اختر مقطعًا أقصر.',
        id: 'Hasilnya akan berukuran ~' + predictedMB + ' MB, sedangkan file terbesar yang bisa dirakit browser ini ~' + capMB + ' MB. Pilih bagian yang lebih pendek.',
        pt: 'O resultado teria ~' + predictedMB + ' MB, e o maior arquivo que este navegador consegue montar é de ~' + capMB + ' MB. Escolha um trecho mais curto.',
        de: 'Das Ergebnis wäre ~' + predictedMB + ' MB groß, dieser Browser kann aber höchstens ~' + capMB + ' MB zusammensetzen. Wählen Sie einen kürzeren Abschnitt.',
        es: 'El resultado tendría ~' + predictedMB + ' MB, y el archivo más grande que este navegador puede ensamblar es de ~' + capMB + ' MB. Elige un fragmento más corto.',
        fr: 'Le résultat ferait ~' + predictedMB + ' Mo, alors que ce navigateur ne peut assembler qu’un fichier de ~' + capMB + ' Mo au plus. Choisissez un passage plus court.',
        uk: 'Результат буде ~' + predictedMB + ' МБ, а зібрати в браузері можна файл до ~' + capMB + ' МБ. Виберіть коротший фрагмент.',
        tr: 'Sonuç ~' + predictedMB + ' MB olur, oysa bu tarayıcının oluşturabileceği en büyük dosya ~' + capMB + ' MB. Daha kısa bir bölüm seçin.'
    };
    return msgs[L] || msgs.en;
}

function checkOutputBytes(opts){
    opts = opts || {};
    var out = +opts.outBytes || 0;
    var wall = outputWallBytes();
    var capMB = Math.ceil(wall / 1048576);
    if (!(out > 0)) return { safe: true, predictedMB: 0, capMB: capMB, reason: 'no_estimate' };
    var predictedMB = Math.ceil(out / 1048576);
    if (out <= wall) return { safe: true, predictedMB: predictedMB, capMB: capMB };
    return {
        safe: false,
        reason: 'output_too_large_for_wasm',
        predictedMB: predictedMB,
        capMB: capMB,
        userMessage: localizedOutputMessage(window.__LOCALE, predictedMB, capMB)
    };
}

window.FFmpegMemoryGuard = {
    check: check,
    assertSafe: assertSafe,
    checkDimensions: checkDimensions,
    assertDimensionsSafe: assertDimensionsSafe,
    checkVideoEncode: checkVideoEncode,
    checkOutputBytes: checkOutputBytes,
    checkStreamingLane: checkStreamingLane,
    operations: Object.keys(OPERATION_MULTIPLIERS).sort(),
    version: '1.3.0'
};

})();
